import { useEffect, useState } from 'react';
import { useParticipants } from '@livekit/components-react';
import { Mic, MicOff, Video, VideoOff, Check, X } from 'lucide-react';
import type { Participant } from '@/features/meetings/meeting.api';
import { livekitApi } from './livekit.api';

/** Right-sidebar People panel: live participants + host waiting-room queue. */
export default function ParticipantsSidebar({
  meetingId,
  isModerator,
}: {
  meetingId: string;
  isModerator: boolean;
}): JSX.Element {
  const participants = useParticipants();
  const [waiting, setWaiting] = useState<Participant[]>([]);

  // Host polls the waiting queue (replaced by sockets in Phase 5).
  useEffect(() => {
    if (!isModerator) return;
    let active = true;
    const refresh = async () => {
      try {
        const res = await fetchWaiting(meetingId);
        if (active) setWaiting(res);
      } catch {
        /* noop */
      }
    };
    void refresh();
    const tick = setInterval(refresh, 4000);
    return () => {
      active = false;
      clearInterval(tick);
    };
  }, [meetingId, isModerator]);

  const admit = async (participantId: string, ok: boolean) => {
    await livekitApi.admit(meetingId, participantId, ok);
    setWaiting((w) => w.filter((p) => p.id !== participantId));
  };

  return (
    <div className="flex h-full flex-col">
      {isModerator && waiting.length > 0 && (
        <div className="border-b border-slate-800 p-3">
          <p className="mb-2 text-xs font-semibold uppercase text-slate-400">Waiting ({waiting.length})</p>
          <ul className="space-y-2">
            {waiting.map((p) => (
              <li key={p.id} className="flex items-center justify-between rounded-lg bg-slate-800 px-3 py-2 text-sm">
                <span>{p.guestName ?? 'Participant'}</span>
                <span className="flex gap-1">
                  <button className="rounded bg-green-600 p-1" onClick={() => admit(p.id, true)}>
                    <Check size={14} />
                  </button>
                  <button className="rounded bg-red-600 p-1" onClick={() => admit(p.id, false)}>
                    <X size={14} />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-3">
        <p className="mb-2 text-xs font-semibold uppercase text-slate-400">In meeting ({participants.length})</p>
        <ul className="space-y-1">
          {participants.map((p) => (
            <li key={p.sid} className="flex items-center justify-between rounded-lg px-2 py-1.5 text-sm text-slate-200">
              <span className="truncate">
                {p.name || p.identity} {p.isLocal && '(you)'}
              </span>
              <span className="flex items-center gap-2 text-slate-400">
                {p.isMicrophoneEnabled ? <Mic size={14} /> : <MicOff size={14} className="text-red-400" />}
                {p.isCameraEnabled ? <Video size={14} /> : <VideoOff size={14} className="text-red-400" />}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

async function fetchWaiting(meetingId: string): Promise<Participant[]> {
  const { api } = await import('@/lib/axios');
  const { data } = await api.get(`/meetings/${meetingId}/participants`);
  return (data.data.participants as Participant[]).filter((p) => p.status === 'WAITING');
}
